import { useEffect, useRef } from 'react';

const KEYBOARD_THRESHOLD_PX = 120;

/**
 * Mirrors the browser's visible viewport onto the widget root.
 *
 * `100dvh` is a useful fallback, but iOS Safari and embedded Android browsers
 * do not update it consistently while the software keyboard or browser chrome
 * is moving. `visualViewport` represents the area the user can actually see.
 */
export function useMobileVisualViewport(enabled: boolean) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled || typeof window === 'undefined') return;

    const viewport = window.visualViewport;
    let frame = 0;

    const applyViewport = () => {
      frame = 0;

      const height = viewport?.height ?? window.innerHeight;
      const offsetTop = viewport?.offsetTop ?? 0;
      const obscuredHeight = Math.max(0, window.innerHeight - height - offsetTop);

      root.style.setProperty(
        '--teo-visual-viewport-height',
        `${Math.round(height)}px`,
      );
      root.style.setProperty(
        '--teo-visual-viewport-offset-top',
        `${Math.max(0, Math.round(offsetTop))}px`,
      );
      root.toggleAttribute(
        'data-teo-keyboard-open',
        obscuredHeight >= KEYBOARD_THRESHOLD_PX,
      );
    };

    const scheduleViewportUpdate = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(applyViewport);
    };

    applyViewport();
    viewport?.addEventListener('resize', scheduleViewportUpdate);
    viewport?.addEventListener('scroll', scheduleViewportUpdate);
    window.addEventListener('resize', scheduleViewportUpdate);
    window.addEventListener('orientationchange', scheduleViewportUpdate);
    window.addEventListener('focusin', scheduleViewportUpdate);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      viewport?.removeEventListener('resize', scheduleViewportUpdate);
      viewport?.removeEventListener('scroll', scheduleViewportUpdate);
      window.removeEventListener('resize', scheduleViewportUpdate);
      window.removeEventListener('orientationchange', scheduleViewportUpdate);
      window.removeEventListener('focusin', scheduleViewportUpdate);
      root.style.removeProperty('--teo-visual-viewport-height');
      root.style.removeProperty('--teo-visual-viewport-offset-top');
      root.removeAttribute('data-teo-keyboard-open');
    };
  }, [enabled]);

  return rootRef;
}
